import React, {ReactElement} from "react";
import {IconButton, TextField, Typography} from "@mui/material";
import {
  DeleteOutlined,
  LockOpenOutlined,
  LockOutlined,
  VisibilityOffOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import {PD_SM, PD_XSM} from "../../shared/dimens";
import {colorHighlightAlt, gray, mediumGray} from "../../shared/colors";
import {CanvasElement} from "./types";

function elementTypeLabel(el: CanvasElement): string {
  switch (el.type) {
    case "image":
      return "Image";
    case "shape":
      return "Shape";
    case "group":
      return "Group";
    case "text":
      return "Text";
    default:
      return "Element";
  }
}

function LayerRow({element, selected, onSelect, onRename, onToggleVisibility, onToggleLock, onDelete}: {
  element: CanvasElement;
  selected: boolean;
  onSelect: () => void;
  onRename: (name: string) => void;
  onToggleVisibility: () => void;
  onToggleLock: () => void;
  onDelete: () => void;
}): ReactElement {
  return (
    <div
      onClick={onSelect}
      style={{
        display: "flex",
        alignItems: "center",
        gap: PD_XSM,
        padding: `${PD_XSM}px ${PD_SM}px`,
        background: selected ? colorHighlightAlt : null,
        borderRadius: 4,
        cursor: "pointer",
        opacity: element.visible ? 1 : 0.5,
      }}>
      <Typography
        variant="caption"
        style={{
          background: gray,
          borderRadius: 4,
          padding: `2px ${PD_XSM}px`,
          whiteSpace: "nowrap",
          fontWeight: 600,
          fontSize: "0.7rem",
        }}>
        {elementTypeLabel(element)}
      </Typography>
      <TextField
        variant="standard"
        size="small"
        value={element.name}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onRename(e.target.value)}
        InputProps={{
          disableUnderline: !selected,
          style: {fontSize: "0.8rem"},
        }}
        style={{flexGrow: 1, minWidth: 0}}
      />
      <IconButton size="small" onClick={(e) => {
        e.stopPropagation();
        onToggleVisibility();
      }}>
        {element.visible
          ? <VisibilityOutlined style={{fontSize: 18}}/>
          : <VisibilityOffOutlined style={{fontSize: 18}}/>}
      </IconButton>
      <IconButton size="small" onClick={(e) => {
        e.stopPropagation();
        onToggleLock();
      }}>
        {element.locked
          ? <LockOutlined style={{fontSize: 18}}/>
          : <LockOpenOutlined style={{fontSize: 18}}/>}
      </IconButton>
      <IconButton size="small" onClick={(e) => {
        e.stopPropagation();
        onDelete();
      }}>
        <DeleteOutlined style={{fontSize: 18}}/>
      </IconButton>
    </div>
  );
}

export function LayersPanel(props: {
  elements: CanvasElement[];
  selectedIds: string[];
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => void;
  onToggleVisibility: (id: string) => void;
  onToggleLock: (id: string) => void;
  onDelete: (id: string) => void;
  onReorder: (elements: CanvasElement[]) => void;
}): ReactElement {
  const {elements, selectedIds, onSelect, onRename, onToggleVisibility, onToggleLock, onDelete} = props;

  // Display in reverse order: last element (topmost on canvas) shown first
  const reversed = [...elements].reverse();

  return (
    <div style={{display: "flex", flexDirection: "column"}}>
      {reversed.length === 0
        ? <Typography variant="caption" style={{color: mediumGray, textAlign: "center"}}>
          No elements yet
        </Typography>
        : reversed.map(el => (
          <LayerRow
            key={el.id}
            element={el}
            selected={selectedIds.includes(el.id)}
            onSelect={() => onSelect(el.id)}
            onRename={(name) => onRename(el.id, name)}
            onToggleVisibility={() => onToggleVisibility(el.id)}
            onToggleLock={() => onToggleLock(el.id)}
            onDelete={() => onDelete(el.id)}
          />
        ))}
    </div>
  );
}
